// Globals
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Modules
import { usePermissions, Resource } from '@/modules/Permissions';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { deleteAdmin, getAdminsList } from '../../feature/actionCreators';
import { usersListSelector } from '../../feature/selectors';
import { changeQuery } from '../../feature';

// Models
import { IAdminProfileResponse } from '@/models/admins.model';

// Components
import {
  StyledContentBlock,
  StyledPageTitle,
  StyledPageWrapper,
  StyledTitleModal,
  StyledButtonsWrapper,
} from '../../components/Styled';
import { StyledCreateButton, StyledHeaderBlock } from './AdminsListContainerStyles';
import AdminsList from '../../components/AdminsList/AdminsList';
import { Button } from '@/ui';
import { PlusCircleIcon } from '@/icons';
import { Modal, ModalSize } from '@/components';

// API
import { UsersAPI } from '@/api';

// Helpers
import { useModal } from '@/hooks';
import GetAdminsListDTO from '../../dtos/GetAdminsListDTO';
import { getUpdatedAdminsList } from '../../helpers';

type TAdminsListContainer = {
  onCreateUser: () => void;
};

const AdminsListContainer = ({ onCreateUser }: TAdminsListContainer) => {
  const dispatch = useAppDispatch();
  const { admins, query } = useAppState(usersListSelector);
  const { canManage } = usePermissions();
  const canManageAdmins = canManage(Resource.ADMIN_PANEL);
  const { t } = useTranslation(['usersList']);
  const [deletedAdmin, setDeletedAdmin] = useState<IAdminProfileResponse | null>(null);
  const { isOpened, openModal, closeModal } = useModal();

  const getAdminsListHandler = (params?: GetAdminsListDTO) => {
    const dto = new GetAdminsListDTO({ page: 1 });
    dispatch(getAdminsList(params ?? dto));
  };

  const handlePageChange = (page: number) => {
    const dto = new GetAdminsListDTO({
      ...query,
      page,
    });

    dispatch(
      changeQuery({
        ...query,
        page,
      }),
    );

    getAdminsListHandler(dto);
  };

  const handleDelete = async () => {
    try {
      if (deletedAdmin) await dispatch(deleteAdmin(deletedAdmin.id));
      getAdminsListHandler();
      closeModal();
      setDeletedAdmin(null);
    } catch {}
  };

  const handleCloseModal = () => {
    closeModal();
    setDeletedAdmin(null);
  };

  const handleResendInvitation = (id: string) => {
    UsersAPI.resendInvitation(id);
  };

  const changeDeletedAdmin = (admin: IAdminProfileResponse) => {
    setDeletedAdmin(admin);
    openModal();
  };

  useEffect(() => {
    handlePageChange(1);
    return () => {
      dispatch(changeQuery({}));
    };
  }, []);

  return (
    <StyledPageWrapper>
      <StyledHeaderBlock>
        <StyledPageTitle>{t('users_list_admins')}</StyledPageTitle>
        {canManageAdmins && (
          <StyledCreateButton onClick={onCreateUser}>
            <PlusCircleIcon />
            {t('users_list_create_user')}
          </StyledCreateButton>
        )}
      </StyledHeaderBlock>
      <StyledContentBlock>
        <AdminsList
          currentPage={admins?.meta?.current_page ?? 1}
          onPageChange={handlePageChange}
          adminsList={getUpdatedAdminsList(admins?.data) ?? []}
          total={admins?.meta?.total ?? 0}
          setDeletedAdmin={changeDeletedAdmin}
          onResendInvitation={handleResendInvitation}
        />
      </StyledContentBlock>
      <Modal visible={isOpened} onCancel={handleCloseModal} size={ModalSize.LARGE}>
        <StyledTitleModal>
          {t('users_list_delete_admin', {
            last_name: deletedAdmin?.last_name,
            first_name: deletedAdmin?.first_name,
          })}
        </StyledTitleModal>
        <StyledButtonsWrapper>
          <Button onClick={handleCloseModal} type="primary">
            {t('users_list_back')}
          </Button>
          <Button onClick={handleDelete} type="text">
            {t('users_list_delete_Button')}
          </Button>
        </StyledButtonsWrapper>
      </Modal>
    </StyledPageWrapper>
  );
};

export default AdminsListContainer;
